import { getAppEnvironment } from '@/config/env-config';

interface ErrorInfo {
  message: string;
  stack?: string;
  componentStack?: string;
  userId?: string;
  userAgent?: string;
  url?: string;
  timestamp: string;
  level: 'error' | 'warning' | 'info';
  tags?: Record<string, string>;
  extra?: Record<string, unknown>;
}

class ErrorTracker {
  private isInitialized = false;
  private userId?: string;
  private environment: string;

  constructor() {
    this.environment = getAppEnvironment();
  }

  init(userId?: string): void {
    this.userId = userId;
    this.isInitialized = true;

    // Set up global error handlers
    this.setupGlobalErrorHandlers();

    console.info(
      'Error tracking initialized for environment:',
      this.environment
    );
  }

  private setupGlobalErrorHandlers(): void {
    window.addEventListener('unhandledrejection', event => {
      this.captureError(new Error(event.reason), {
        level: 'error',
        tags: { type: 'unhandledrejection' },
      });
    });

    window.addEventListener('error', event => {
      this.captureError(event.error ?? new Error(event.message), {
        level: 'error',
        tags: { type: 'global' },
        extra: {
          filename: event.filename,
          lineno: event.lineno,
          colno: event.colno,
        },
      });
    });
  }

  captureError(
    error: Error | string,
    options: Partial<Omit<ErrorInfo, 'message' | 'timestamp'>> = {}
  ): void {
    if (!this.isInitialized) {
      console.warn('Error tracking not initialized');

      return;
    }

    const err = typeof error === 'string' ? new Error(error) : error;

    const errorInfo: ErrorInfo = {
      message: err.message,
      stack: err.stack,
      componentStack: options.componentStack,
      userId: this.userId,
      userAgent: navigator.userAgent,
      url: window.location.href,
      timestamp: new Date().toISOString(),
      level: options.level ?? 'error',
      tags: options.tags ?? {},
      extra: options.extra ?? {},
    };

    // In development, log to console
    if (this.environment === 'development') {
      console.group('Error Captured');
      console.error('Message:', errorInfo.message);
      console.error('Stack:', errorInfo.stack);
      console.error('Component Stack:', errorInfo.componentStack);
      console.error('Tags:', errorInfo.tags);
      console.error('Extra:', errorInfo.extra);
      console.groupEnd();
    }
  }

  setUser(userId: string): void {
    this.userId = userId;
  }

  addTag(_key: string, _value: string): void {
    // Add tags to future error reports
  }

  // React Error Boundary integration
  captureReactError(error: Error, errorInfo: { componentStack: string }): void {
    this.captureError(error, {
      level: 'error',
      componentStack: errorInfo.componentStack,
      tags: { type: 'react' },
    });
  }
}

// Create singleton instance
export const errorTracker = new ErrorTracker();

// React Error Boundary hook
export const useErrorTracking = () => {
  return {
    captureError: errorTracker.captureError.bind(errorTracker),
    setUser: errorTracker.setUser.bind(errorTracker),
    addTag: errorTracker.addTag.bind(errorTracker),
  };
};

// Initialize error tracking
errorTracker.init();
